import type { LoyaltyTotals } from '~/hooks/apis/reporting/useLoyaltyAnalytics';

export type SumKey = keyof LoyaltyTotals;
/** A rate is the sum of its numerator keys over the sum of its denominator keys across a bucket's days, never an average of daily rates. */
export type RatioMeasure = { numerator: SumKey[]; denominator: SumKey[] };
/**
 * A count on a day (members), not a sum over days: a week or month reads the value on its last day, and a running
 * total means nothing, so Cumulative is not offered.
 */
export type StockMeasure = { stock: SumKey };
export type Measure = SumKey | RatioMeasure | StockMeasure;

export const isStockMeasure = (measure: Measure | undefined): measure is StockMeasure =>
  typeof measure === 'object' && 'stock' in measure;
export const isRatioMeasure = (measure: Measure | undefined): measure is RatioMeasure =>
  typeof measure === 'object' && 'numerator' in measure;

/**
 * Member keys come from the member sources, not the points or renewal rows, so a day with no such rows (hasData false)
 * still carries them.
 */
export const MEMBER_KEYS: ReadonlySet<SumKey> = new Set<SumKey>([
  'members',
  'newMembers',
  'membersBronze',
  'membersSilver',
  'membersGold',
  'membersPlatinum',
  'membersInfinite',
]);

/** Société tiers in ladder order, as the API names the per-tier member keys (membersBronze and so on). */
export const SOCIETE_TIERS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Infinite'] as const;

export const STOCK_MEASURES: Record<string, StockMeasure> = {
  'loyalty.luxplus_members': { stock: 'members' },
  'loyalty.societe_members': { stock: 'members' },
  ...Object.fromEntries(
    SOCIETE_TIERS.map((tier) => [
      `loyalty.societe_members_${tier.toLowerCase()}`,
      { stock: `members${tier}` },
    ]),
  ),
};

export const RATIO_MEASURES: Record<string, RatioMeasure> = {
  'loyalty.burn_ratio': { numerator: ['burned'], denominator: ['earned'] },
  'loyalty.redemption_ratio': { numerator: ['redeemed'], denominator: ['earned'] },
  'loyalty.earn_rate': { numerator: ['earnedOnSales'], denominator: ['saleRowSales'] },
  'loyalty.renewal_rate': { numerator: ['renewed'], denominator: ['renewed', 'cancelled', 'lapsed'] },
};

const SUM_MEASURES: Record<string, SumKey> = {
  'loyalty.points_earned': 'earned',
  'loyalty.points_burned': 'burned',
  'loyalty.points_redeemed': 'redeemed',
  'loyalty.periods_renewed': 'renewed',
  'loyalty.periods_renewed_then_cancelled': 'cancelled',
  'loyalty.periods_lapsed': 'lapsed',
  'loyalty.periods_awaiting': 'other',
  'loyalty.luxplus_new_members': 'newMembers',
};

/** What the trend chart can draw for a metric id, or undefined when the API sends no per-day components for it. */
export const trendMeasure = (id: string): Measure | undefined =>
  SUM_MEASURES[id] ?? RATIO_MEASURES[id] ?? STOCK_MEASURES[id];

/** Null when any key is missing, so a day the API did not fully populate never contributes half a ratio. */
export function sumKeys(point: LoyaltyTotals, keys: ReadonlyArray<SumKey>): number | null {
  let total = 0;
  for (const key of keys) {
    const value = point[key];
    if (value == null) return null;
    total += value;
  }
  return total;
}
